/**
 * Equation Button Component
 * Button for inserting inline and display equations
 */

import React, { useState, useEffect } from 'react'
import { EditorView } from '@codemirror/view'
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { getSelection } from '@/utils/latex/insertionHelpers'
import AIEquationGenerator from './AIEquationGenerator'

interface EquationButtonProps {
  view: EditorView | null
  theme?: 'light' | 'dark'
  documentId?: string
}

type EquationType = 'inline' | 'display' | 'numbered' | 'align'

const EquationButton: React.FC<EquationButtonProps> = ({ view, theme = 'light', documentId }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isAIGeneratorOpen, setIsAIGeneratorOpen] = useState(false)
  const [equationType, setEquationType] = useState<EquationType>('inline')
  const [equationText, setEquationText] = useState('')

  useEffect(() => {
    if (isDialogOpen && view) {
      const selection = getSelection(view)
      if (selection.text) {
        setEquationText(selection.text)
      }
      setTimeout(() => {
        const input = document.getElementById('equation-input')
        if (input) {
          (input as HTMLInputElement).focus()
        }
      }, 100)
    } else {
      setEquationText('')
    }
  }, [isDialogOpen, view])

  const handleInsert = () => {
    if (!view) return

    const selection = getSelection(view)
    let insertText = ''

    switch (equationType) {
      case 'inline':
        insertText = `$${equationText || ''}$`
        break
      case 'display':
        insertText = `\\[${equationText || ''}\\]`
        break
      case 'numbered':
        insertText = `\\begin{equation}\n  ${equationText || ''}\n\\end{equation}`
        break
      case 'align':
        insertText = `\\begin{align}\n  ${equationText || ''}\n\\end{align}`
        break
    }

    const cursorPos = selection.from + insertText.length - (equationType === 'numbered' || equationType === 'align' ? 1 : 0)

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: insertText
      },
      selection: {
        anchor: cursorPos
      }
    })

    view.focus()
    setIsDialogOpen(false)
  }

  const handleAIInsert = (latexCode: string, aiEquationType?: 'inline' | 'display' | 'numbered' | 'align') => {
    if (!view) return

    const selection = getSelection(view)
    
    // Clean up the LaTeX code: remove any existing mismatched or incorrect delimiters
    let cleanedCode = latexCode.trim()
    
    // Remove markdown code blocks if present
    if (cleanedCode.startsWith('```latex')) {
      cleanedCode = cleanedCode.replace(/^```latex\n?/, '').replace(/\n?```$/, '')
    } else if (cleanedCode.startsWith('```')) {
      cleanedCode = cleanedCode.replace(/^```\n?/, '').replace(/\n?```$/, '')
    }
    
    // Fix mismatched delimiters: remove any existing math delimiters
    // This handles cases where AI generates mixed delimiters like \( ... \]
    cleanedCode = cleanedCode
      .replace(/^\\?\[/, '')  // Remove leading \[
      .replace(/\\?\]$/, '')  // Remove trailing \]
      .replace(/^\\?\(/, '')  // Remove leading \(
      .replace(/\\?\)$/, '')  // Remove trailing \)
      .replace(/^\$+/, '')    // Remove leading $
      .replace(/\$+$/, '')    // Remove trailing $
      .trim()
    
    // Remove equation environments if present (we'll re-wrap based on user preference)
    cleanedCode = cleanedCode
      .replace(/^\\begin\{equation\*?\}\s*/i, '')
      .replace(/\s*\\end\{equation\*?\}$/i, '')
      .replace(/^\\begin\{align\*?\}\s*/i, '')
      .replace(/\s*\\end\{align\*?\}$/i, '')
      .trim()
    
    // Use the equation type from AI generator if provided, otherwise fall back to current dialog preference
    const finalEquationType = aiEquationType || equationType
    
    // Determine equation type based on the cleaned code and user preference
    let wrappedCode = cleanedCode
    if (!cleanedCode.includes('\\begin{')) {
      // If it's plain math, wrap it based on selected equation type
      if (finalEquationType === 'inline') {
        wrappedCode = `$${cleanedCode}$`
      } else if (finalEquationType === 'display') {
        wrappedCode = `\\[${cleanedCode}\\]`
      } else if (finalEquationType === 'numbered') {
        wrappedCode = `\\begin{equation}\n  ${cleanedCode}\n\\end{equation}`
      } else {
        wrappedCode = `\\begin{align}\n  ${cleanedCode}\n\\end{align}`
      }
    }
    
    const cursorPos = selection.from + wrappedCode.length

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: wrappedCode + '\n\n'
      },
      selection: {
        anchor: cursorPos + 2
      }
    })

    view.focus()
  }

  const buttonClass = clsx(
    'p-1.5 rounded transition-colors',
    theme === 'dark'
      ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
      : 'text-gray-800 hover:bg-gray-100 hover:text-gray-900',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  )

  const dialogClass = clsx(
    'fixed inset-0 z-50 flex items-center justify-center',
    theme === 'dark' ? 'bg-black/50' : 'bg-black/30'
  )

  const contentClass = clsx(
    'relative rounded-lg shadow-xl p-6 w-full max-w-md',
    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
  )

  const inputClass = clsx(
    'w-full px-3 py-2 rounded-md border text-sm font-mono',
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
  )

  return (
    <>
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          disabled={!view}
          className={buttonClass}
        title="Insert equation (Ctrl+M or Cmd+M) - Insert inline, display, numbered, or align equations"
        aria-label="Insert equation"
        >
          <span className="text-sm font-mono">∑</span>
        </button>
        <button
          type="button"
          onClick={() => setIsAIGeneratorOpen(true)}
          disabled={!view}
          className={buttonClass}
          title="AI: Generate equation from natural language description or image"
          aria-label="AI equation generator"
        >
          <SparklesIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {isDialogOpen && (
        <div className={dialogClass} onClick={() => setIsDialogOpen(false)}>
          <div className={contentClass} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsDialogOpen(false)}
              className={clsx(
                'absolute top-4 right-4 p-1 rounded-md',
                theme === 'dark' ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
              )}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <h3 className={clsx('text-lg font-semibold mb-4', theme === 'dark' ? 'text-gray-100' : 'text-gray-900')}>
              Insert Equation
            </h3>

            <div className="space-y-4">
              <div>
                <label className={clsx('block text-sm font-medium mb-2', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  Equation Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['inline', 'display', 'numbered', 'align'] as EquationType[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => setEquationType(type)}
                      className={clsx(
                        'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                        equationType === type
                          ? 'bg-primary-600 text-white'
                          : theme === 'dark'
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                  LaTeX Math
                </label>
                <input
                  id="equation-input"
                  type="text"
                  value={equationText}
                  onChange={(e) => setEquationText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault()
                      handleInsert()
                    } else if (e.key === 'Escape') {
                      setIsDialogOpen(false)
                    }
                  }}
                  placeholder="x^2 + y^2 = r^2"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsDialogOpen(false)}
                className={clsx('px-4 py-2 rounded-md text-sm', theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100')}
              >
                Cancel
              </button>
              <button
                onClick={handleInsert}
                className="px-4 py-2 rounded-md text-sm bg-primary-600 text-white hover:bg-primary-700"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}

      {isAIGeneratorOpen && (
        <AIEquationGenerator
          view={view}
          theme={theme}
          documentId={documentId}
          onClose={() => setIsAIGeneratorOpen(false)}
          onInsert={handleAIInsert}
        />
      )}
    </>
  )
}

export default EquationButton

